import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "api/axios";
import Loader from "components/Loader";
import { Agent, AgentWorkflow, FileSystemItem } from "types";
import toast from "react-hot-toast";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation } from "react-i18next";

interface AgentDetailsContextType {
  agentInfo: Agent | null;
  knowledgeBase: FileSystemItem[];
  orignalKnowledgeBase: FileSystemItem[];
  setKnowledgeBase: (f: FileSystemItem[]) => void;
  isLoading: boolean;
  error: string | null;
  editAgent: (data: Partial<Agent>) => Promise<void>;
  updateAgentChunkFileSize: (chunk_file_size: number) => Promise<void>;
  archiveAgent: () => Promise<void>;
  unArchiveAgent?: () => Promise<void>;
  updateAgentDocuments: (
    document_ids: string[],
    remove_agent_document_ids: string[],
    is_regenerate_suggestions?: boolean
  ) => Promise<void>;

  workflows: any[];
  setWorkflows: (workflows: any[]) => void;
  originalWorkflows: any[];
  updateAgentWorkflows: (
    workflow_ids: string[],
    remove_agent_workflow_ids: string[]
  ) => Promise<void>;
  setAgentDetails: (agent: Agent | null) => void;
  logsReloadKey: number;
  reloadLogs: () => void;
}

const AgentDetailsContext = createContext<AgentDetailsContextType | undefined>(
  undefined
);

export const useAgentInfo = () => {
  const context = useContext(AgentDetailsContext);
  if (!context) {
    throw new Error("useAgentInfo must be used within an AgentDetailsProvider");
  }
  return context;
};

interface AgentDetailsProviderProps {
  children: ReactNode;
}

export const AgentInfoProvider = ({ children }: AgentDetailsProviderProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [agentInfo, setAgentDetails] = useState<Agent | null>(null);
  const [knowledgeBase, setKnowledgeBase] = useState<FileSystemItem[]>([]);
  const [orignalKnowledgeBase, setOrignalKnowledgeBase] = useState<
    FileSystemItem[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [workflows, setWorkflows] = useState<any[]>([]);
  const [originalWorkflows, setOriginalWorkflows] = useState<any[]>([]);
  const [logsReloadKey, setLogsReloadKey] = useState(0);

  const fetchAgentDetails = useCallback(
    async (signal?: AbortSignal) => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      // If signal is already aborted, don't proceed
      if (signal?.aborted) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      const maxRetries = 3;
      const retryDelay = 1000;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          // Check if request was aborted before making the request
          if (signal?.aborted) {
            setIsLoading(false);
            return;
          }

          const [agentResponse, documentsResponse, workflowResponse] =
            await Promise.all([
              api.get<Agent>(`/agents/${id}`, {
                ...(signal ? { signal } : {}),
              }),
              api.get<FileSystemItem[]>(`/agents/${id}/documents`, {
                ...(signal ? { signal } : {}),
              }),
              api.get<AgentWorkflow[]>(`/agents/${id}/workflows`, {
                ...(signal ? { signal } : {}),
              }),
            ]);

          // Check if request was aborted after the requests complete
          if (signal?.aborted) {
            setIsLoading(false);
            return;
          }

          setOrignalKnowledgeBase(documentsResponse.data);
          setAgentDetails(agentResponse.data);
          setKnowledgeBase(documentsResponse.data);
          // Map the response to match the expected structure
          const workflows = [...workflowResponse.data];
          setWorkflows(workflows);
          setOriginalWorkflows(workflows);
          setIsLoading(false);
          return; // Success
        } catch (err: any) {
          if (
            signal?.aborted ||
            err.name === "CanceledError" ||
            err.code === "ERR_CANCELED"
          ) {
            setIsLoading(false);
            return; // Aborted, don't set error or retry
          }

          if (err.response?.status === 500 && attempt < maxRetries) {
            // Wait before retrying
            await new Promise((resolve) => setTimeout(resolve, retryDelay));
            // Continue to next iteration
            continue;
          } else {
            setError("Failed to fetch agent details");
            console.error("Error fetching agent details:", err);
            toast.error("Something went wrong while loading agent");
            setIsLoading(false);
            navigate("/", { replace: true });
            return;
          }
        }
      }
      setIsLoading(false);
    },
    [id, navigate]
  );

  const lastFetchedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!id) {
      lastFetchedIdRef.current = null;
      // Clear state when id is not available
      setAgentDetails(null);
      setKnowledgeBase([]);
      setOrignalKnowledgeBase([]);
      setWorkflows([]);
      setOriginalWorkflows([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    if (lastFetchedIdRef.current === id) {
      return;
    }

    lastFetchedIdRef.current = id;

    // Reset agent info when id changes to ensure fresh data is loaded
    setAgentDetails(null);
    setKnowledgeBase([]);
    setOrignalKnowledgeBase([]);
    setWorkflows([]);
    setOriginalWorkflows([]);
    setError(null);
    setIsLoading(true);

    const abortController = new AbortController();
    fetchAgentDetails(abortController.signal);
    return () => abortController.abort();
  }, [id, fetchAgentDetails]);

  const editAgent = async (data: Partial<Agent>) => {
    try {
      setError(null);
      await api.put<Agent>(`/agents/${id}`, data);
      setAgentDetails((prev) =>
        prev
          ? {
              ...prev,
              ...(data.name && { name: data.name }),
              ...(data.description && { description: data.description }),
              ...(data.chunk_file_size !== undefined && {
                chunk_file_size: data.chunk_file_size,
              }),
            }
          : null
      );
    } catch (err) {
      setError("Failed to update agent details");
      handleAxiosAndCaughtError(err);
    }
  };

  const updateAgentChunkFileSize = async (chunk_file_size: number) => {
    try {
      await api.patch(`/agents/${id}/chunk-file-size`, {
        chunk_file_size,
      });
      setAgentDetails((prev) =>
        prev
          ? {
              ...prev,
              chunk_file_size: chunk_file_size,
            }
          : null
      );
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  };

  const archiveAgent = async () => {
    try {
      setError(null);
      await api.put(`/agents/${id}/archive`);

      setAgentDetails((prev) => {
        return {
          ...prev,
          is_archived: true,
        };
      });
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  };
  const unArchiveAgent = async () => {
    try {
      setError(null);
      await api.put(`/agents/${id}/unarchive`);
      setAgentDetails((prev) => {
        return {
          ...prev,
          is_archived: false,
        };
      });
    } catch (err) {
      handleAxiosAndCaughtError(err);
    }
  };
  const updateAgentDocuments = async (
    newDocumentIds: string[],
    removeDocumentIds: string[],
    is_regenerate_suggestions: boolean = true
  ) => {
    try {
      setIsLoading(true);
      setError(null);
      await api.put(`/agents/${id}/documents`, {
        document_ids: newDocumentIds,
        remove_agent_document_ids: removeDocumentIds,
        is_regenerate_suggestions,
      });
      const newKnowledgebase = knowledgeBase.filter(
        (k) => !removeDocumentIds.includes(k.agent_document_id)
      );
      setKnowledgeBase(newKnowledgebase);
      setOrignalKnowledgeBase(newKnowledgebase);
      await fetchAgentDetails();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoading(false);
    }
  };
  const updateAgentWorkflows = async (
    workflow_ids: string[],
    remove_agent_workflow_ids: string[]
  ) => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      await api.put(`/agents/${id}/workflows`, {
        workflow_ids,
        remove_agent_workflow_ids,
      });

      const newWorkflow = workflows.filter(
        (k) => !remove_agent_workflow_ids.includes(k.agent_workflow_id)
      );

      setWorkflows(newWorkflow);
      setOriginalWorkflows(newWorkflow);
      await fetchAgentDetails();

      toast.success(t("agents.workflows.updateSuccess"));
    } catch (error) {
      handleAxiosAndCaughtError(error);
    }
  };

  const reloadLogs = useCallback(() => {
    setLogsReloadKey((prev) => prev + 1);
  }, []);

  const value = {
    agentInfo,
    isLoading,
    error,
    editAgent,
    updateAgentChunkFileSize,
    archiveAgent,
    knowledgeBase,
    updateAgentDocuments,
    setKnowledgeBase,
    unArchiveAgent,
    orignalKnowledgeBase,
    workflows,
    setWorkflows,
    originalWorkflows,
    updateAgentWorkflows,
    setAgentDetails,
    logsReloadKey,
    reloadLogs,
  };

  return (
    <AgentDetailsContext.Provider value={value}>
      {isLoading ? (
        <Loader
          txt="Loading agent..."
          className="absolute left-1/2 top-1/2 -translate-y-1/2 translate-x-1/2"
        />
      ) : (
        children
      )}
    </AgentDetailsContext.Provider>
  );
};
